import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { usePurchaseRequestListQuery } from 'api';
import { TablePaginationConfig, TableProps } from 'components';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { OrderType, PaginationType, SortParamsType } from 'typings';
import { PurchaseRequestDataType } from './index';

export interface IFilterParams {
  name?: string;
  tags?: string[];
  countryId?: string;
  industryId?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  minPurchaseRequests?: number;
  maxPurchaseRequests?: number;
}

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export const usePurchaseRequest = (forDashboard?: boolean) => {
  const [addModalState, setAddModalState] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState<IFilterParams>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [sort, setSort] = useState<SortParamsType>(forDashboard ? { field: 'status', order: 'asc' } : initialSort);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const { data: { content = [], totalElements = 0 } = {}, isLoading } = usePurchaseRequestListQuery({
    size: pagination.size,
    page: pagination.page,
    sortField: forDashboard && !sort.field ? 'status' : sort.field,
    sortOrder: forDashboard && !sort.field ? 'asc' : sort.order,
    search,
    filters,
    forDashboard,
  });
  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);
  const onAddModalStateChange = useCallback((value: boolean) => setAddModalState(value), []);
  const routeChange = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.purchaseRequest.view}/${value}/general`);
    },
    [navigate]
  );
  const onRedirectToBrand = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.brand.view}/${value}/management`);
    },
    [navigate]
  );
  const onRedirectToClient = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.client}/${value}/general`);
    },
    [navigate]
  );
  const onRedirectToUser = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.users.view}/${value}/account`);
    },
    [navigate]
  );
  const handleTableChange: TableProps<PurchaseRequestDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<PurchaseRequestDataType> | SorterResult<PurchaseRequestDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );
  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.brandId && { brandId: values.brandId }),
        ...(values.clientId && { clientId: values.clientId }),
        ...(values.countryId && { countryId: values.countryId }),
        ...(values.purchaseManagerId && { purchaseManagerId: values.purchaseManagerId }),
        ...(values.status?.length && { statuses: values.status }),
        ...(values.createDate?.[0] && {
          createdDateFrom: dateFormatter(values.createDate[0], 'start'),
        }),
        ...(values.createDate?.[1] && {
          createdDateTo: dateFormatter(values.createDate[1], 'end'),
        }),
        ...(values.lastUpdatedDate?.[0] && {
          lastUpdatedDateFrom: dateFormatter(values.lastUpdatedDate[0], 'start'),
        }),
        ...(values.lastUpdatedDate?.[1] && {
          lastUpdatedDateTo: dateFormatter(values.lastUpdatedDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const resetFilters = useCallback(() => setFilters({}), []);

  return {
    onOpenFilters,
    onCloseFilters,
    onAddModalStateChange,
    addModalState,
    routeChange,
    data: content?.length ? content[0].purchaseRequestResponses : [],
    total: totalElements ?? 0,
    handleTableChange,
    openFilters,
    applyFilters,
    resetFilters,
    pagination,
    isLoading,
    onRedirectToBrand,
    onRedirectToUser,
    onRedirectToClient,
    onSearch,
  };
};
